import React, { useEffect, useRef, useState } from "react";
import mermaid from "mermaid";

const DiagramViewer = () => {
  const [activeTab, setActiveTab] = useState(0);
  const diagramRefs = useRef([]);

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: true,
      theme: "default",
      securityLevel: "loose",
      fontFamily: "Arial, sans-serif",
      fontSize: 14,
    });
  }, []);

  useEffect(() => {
    if (diagramRefs.current[activeTab]) {
      mermaid.run({
        nodes: [diagramRefs.current[activeTab]],
      });
    }
  }, [activeTab]);

  const diagrams = [
    {
      title: "Diagram 1: Complete System Architecture",
      description:
        "Comprehensive overview of NephroTrax platform showing frontend, backend, database, and external integrations.",
      code: `graph TB
    subgraph "Users"
        P[Patients]
        R[Researchers]
        S[Site Coordinators]
        A[Administrators]
    end

    subgraph "Frontend Layer - AWS S3/CloudFront"
        UI[React Application<br/>Material-UI Components]
        subgraph "Pages"
            PP[Patient Profile<br/>5-Tab System]
            TM[Trial Matching<br/>Interface]
            TS[Trial Search<br/>& Discovery]
            ED[Educational<br/>Content]
            CT[Contact Form]
        end
    end

    subgraph "API Gateway / Load Balancer"
        LB[HTTPS/SSL<br/>Route 53 DNS]
    end

    subgraph "Backend Layer - AWS EC2"
        API[Spring Boot API<br/>REST Endpoints]
        subgraph "Controllers"
            PC[Patient Controller]
            TC[Trial Controller]
            MC[Matching Controller]
            CC[Contact Controller]
        end
        subgraph "Services"
            PS[Patient Service]
            TS2[Trial Service]
            MS[Matching Engine]
            ES[Email Service]
            AS[Aggregation Service]
        end
    end

    subgraph "Data Layer - AWS RDS"
        DB[(PostgreSQL Database<br/>clinical_trial_db)]
        subgraph "Tables"
            PT[patient]
            TT[clinical_trial]
            LT[location]
            MT[matches]
            VT[vitals]
        end
    end

    subgraph "External Integrations"
        CTG[ClinicalTrials.gov<br/>API]
        SMTP[Gmail SMTP<br/>Email Service]
        EU[EU Clinical Trials<br/>Register]
        WHO[WHO ICTRP]
        OTHER[Other Sources<br/>Source N]
    end

    P --> UI
    R --> UI
    S --> UI
    A --> UI
    
    UI --> PP
    UI --> TM
    UI --> TS
    UI --> ED
    UI --> CT
    
    PP --> LB
    TM --> LB
    TS --> LB
    CT --> LB
    
    LB --> API
    
    API --> PC
    API --> TC
    API --> MC
    API --> CC
    
    PC --> PS
    TC --> TS2
    MC --> MS
    CC --> ES
    
    PS --> DB
    TS2 --> DB
    MS --> DB
    ES --> SMTP
    AS --> DB
    
    DB --> PT
    DB --> TT
    DB --> LT
    DB --> MT
    DB --> VT
    
    AS --> CTG
    AS --> EU
    AS --> WHO
    AS --> OTHER

    style P fill:#e1f5ff
    style R fill:#e1f5ff
    style S fill:#e1f5ff
    style A fill:#e1f5ff
    style UI fill:#1A5EA8
    style API fill:#0B2545
    style DB fill:#11998e
    style MS fill:#f093fb
    style AS fill:#4facfe`,
    },
    {
      title:
        "Diagram 2: Multi-Source Clinical Trial Aggregation & Intelligent Matching",
      description:
        "Core innovation showing how multiple trial sources are aggregated, deduplicated, and matched with patients to provide recommendations.",
      code: `graph TB
    subgraph "Clinical Trial Data Sources"
        SA[Source A<br/>ClinicalTrials.gov<br/>US Trials]
        SB[Source B<br/>EU Clinical Trials<br/>Register]
        SC[Source C<br/>WHO ICTRP<br/>International]
        SD[Source D<br/>Pharmaceutical<br/>Company DBs]
        SE[Source E<br/>Research<br/>Institutions]
        SN[Source N<br/>Other Sources]
    end

    subgraph "Data Ingestion Layer"
        API1[REST API Connector]
        API2[REST API Connector]
        API3[REST API Connector]
        API4[REST API Connector]
        API5[REST API Connector]
        API6[REST API Connector]
    end

    subgraph "Aggregation & Processing Engine"
        AGG[Multi-Source Aggregator]
        DEDUP[Deduplication Algorithm<br/>Fuzzy Matching]
        NORM[Data Normalization]
        VALID[Validation & Quality Check]
        ENRICH[Data Enrichment]
    end

    subgraph "Central Clinical Trials Hub"
        CENTRAL[(Unified Trial Database<br/>Deduplicated & Normalized)]
        META[Trial Metadata<br/>Source Provenance<br/>Last Updated<br/>Reliability Score]
    end

    subgraph "Patient Data Input"
        PDB[(Patient Database)]
        PROF[Patient Profile<br/>Demographics<br/>Medical History<br/>Medications<br/>Allergies<br/>Vitals]
    end

    subgraph "Intelligent Matching Engine"
        MATCH[Multi-Criteria Matcher]
        subgraph "Matching Algorithms"
            GEO[Geographic<br/>Distance Calc]
            MED[Medical Eligibility<br/>Criteria Parser]
            TIME[Temporal Matching<br/>Age, Timeline]
            CONTRA[Contraindication<br/>Checker]
            RARE[Rare Disease<br/>Optimizer]
        end
        SCORE[Confidence Scoring<br/>Algorithm]
        RANK[Ranking Engine]
    end

    subgraph "Output & Recommendations"
        PAT_OUT[Patient Recommendations]
        subgraph "Patient View"
            TOP[Top Matching Trials]
            NEARBY[Nearest Sites]
            ELIG[Eligibility Status]
            CONTACT[Contact Info]
        end
        
        RES_OUT[Researcher Dashboard]
        subgraph "Researcher View"
            POOL[Patient Pool]
            METRICS[Matching Metrics]
            SITES[Site Coordination]
            ENROLL[Enrollment Status]
        end
    end

    subgraph "Feedback & Learning"
        FEED[User Feedback Loop]
        ML[Machine Learning<br/>Weight Optimization]
        ANALYTICS[Analytics Engine]
    end

    SA --> API1
    SB --> API2
    SC --> API3
    SD --> API4
    SE --> API5
    SN --> API6

    API1 --> AGG
    API2 --> AGG
    API3 --> AGG
    API4 --> AGG
    API5 --> AGG
    API6 --> AGG

    AGG --> DEDUP
    DEDUP --> NORM
    NORM --> VALID
    VALID --> ENRICH

    ENRICH --> CENTRAL
    CENTRAL --> META

    PDB --> PROF
    PROF --> MATCH
    CENTRAL --> MATCH

    MATCH --> GEO
    MATCH --> MED
    MATCH --> TIME
    MATCH --> CONTRA
    MATCH --> RARE

    GEO --> SCORE
    MED --> SCORE
    TIME --> SCORE
    CONTRA --> SCORE
    RARE --> SCORE

    SCORE --> RANK

    RANK --> PAT_OUT
    RANK --> RES_OUT

    PAT_OUT --> TOP
    PAT_OUT --> NEARBY
    PAT_OUT --> ELIG
    PAT_OUT --> CONTACT

    RES_OUT --> POOL
    RES_OUT --> METRICS
    RES_OUT --> SITES
    RES_OUT --> ENROLL

    PAT_OUT --> FEED
    RES_OUT --> FEED
    FEED --> ML
    ML --> MATCH
    FEED --> ANALYTICS
    ANALYTICS --> CENTRAL

    style SA fill:#4FC3F7
    style SB fill:#4FC3F7
    style SC fill:#4FC3F7
    style SD fill:#4FC3F7
    style SE fill:#4FC3F7
    style SN fill:#4FC3F7
    style AGG fill:#1A5EA8
    style DEDUP fill:#0B2545
    style CENTRAL fill:#11998e
    style MATCH fill:#f093fb
    style SCORE fill:#FA709A
    style PAT_OUT fill:#FCCB90
    style RES_OUT fill:#FCCB90
    style ML fill:#4facfe`,
    },
    {
      title: "Diagram 3: Patent-Focused Innovation Flow",
      description:
        "Key patentable innovations showing the flow from multi-source aggregation through rare disease optimization to intelligent matching and site coordination.",
      code: `graph LR
    subgraph "Innovation 1: Multi-Source Aggregation"
        M1[Multiple Heterogeneous<br/>Data Sources]
        M2[Intelligent<br/>Deduplication]
        M3[Conflict<br/>Resolution]
        M4[Unified<br/>Database]
    end

    subgraph "Innovation 2: Rare Disease Optimization"
        R1[Low-Enrollment<br/>Trial Detection]
        R2[Specialized<br/>Weighting]
        R3[Patient Registry<br/>Integration]
        R4[Physician<br/>Notification]
    end

    subgraph "Innovation 3: Intelligent Matching"
        I1[Multi-Dimensional<br/>Patient Profile]
        I2[NLP Eligibility<br/>Parser]
        I3[Confidence<br/>Scoring]
        I4[Explanation<br/>Generation]
    end

    subgraph "Innovation 4: Site Coordination"
        S1[Three-Way<br/>Optimization]
        S2[Real-Time<br/>Capacity]
        S3[Geographic<br/>Routing]
        S4[Automated<br/>Notification]
    end

    M1 --> M2 --> M3 --> M4
    R1 --> R2 --> R3 --> R4
    I1 --> I2 --> I3 --> I4
    S1 --> S2 --> S3 --> S4

    M4 --> I1
    R4 --> I2
    I4 --> S1

    style M1 fill:#1A5EA8
    style R1 fill:#0B2545
    style I1 fill:#f093fb
    style S1 fill:#4facfe`,
    },
    {
      title: "Diagram 4: Data Flow - Patient Journey",
      description:
        "Sequence diagram showing the complete patient journey from registration through trial matching to researcher notification.",
      code: `sequenceDiagram
    participant Patient
    participant UI as Frontend UI
    participant API as Backend API
    participant Match as Matching Engine
    participant DB as Trial Database
    participant Email as Notification System
    participant Researcher

    Patient->>UI: Register & Create Profile
    UI->>API: POST /patients
    API->>DB: Store Patient Data
    DB-->>API: Patient ID
    API-->>UI: Success + Patient ID
    
    Patient->>UI: Request Trial Matches
    UI->>API: GET /patients/{id}/match-trials
    API->>DB: Fetch Patient Profile
    DB-->>API: Patient Data
    API->>DB: Fetch Available Trials
    DB-->>API: Trial List
    
    API->>Match: Execute Matching Algorithm
    Match->>Match: Geographic Distance Calc
    Match->>Match: Medical Eligibility Check
    Match->>Match: Contraindication Analysis
    Match->>Match: Confidence Scoring
    Match-->>API: Ranked Trial Matches
    
    API-->>UI: Top Matches + Confidence
    UI-->>Patient: Display Results
    
    Patient->>UI: Express Interest in Trial
    UI->>API: POST /patients/{id}/express-interest
    API->>Email: Notify Site Coordinator
    Email->>Researcher: New Patient Interest
    API->>Email: Confirm to Patient
    Email->>Patient: Confirmation + Next Steps
    
    Researcher->>UI: Review Patient Pool
    UI->>API: GET /trials/{id}/matches
    API->>DB: Fetch Matched Patients
    DB-->>API: Patient List
    API-->>UI: Eligible Patient Pool
    UI-->>Researcher: Display Matches`,
    },
    {
      title: "Diagram 5: AWS Deployment Architecture",
      description:
        "Production infrastructure showing AWS services, security groups, and monitoring setup.",
      code: `graph TB
    subgraph "User Access"
        USERS[End Users<br/>Patients, Researchers]
    end

    subgraph "AWS Route 53"
        DNS[DNS Management<br/>nephrotrax.com<br/>api.nephrotrax.com]
    end

    subgraph "AWS CloudFront CDN"
        CF[Global Content Delivery<br/>SSL/TLS Termination<br/>Caching]
    end

    subgraph "AWS S3"
        S3[Static Website Hosting<br/>React Build Files<br/>amyloidcare-frontend]
    end

    subgraph "AWS EC2 - Application Tier"
        EC2[t3.small Instance<br/>Spring Boot Application<br/>Java 17 Runtime]
        SYSTEMD[Systemd Service<br/>Auto-restart<br/>Logging]
    end

    subgraph "AWS RDS - Database Tier"
        RDS[PostgreSQL 14<br/>db.t3.micro<br/>clinical_trial_db]
        BACKUP[Automated Backups<br/>Point-in-Time Recovery]
    end

    subgraph "External Services"
        GMAIL[Gmail SMTP<br/>Email Delivery]
        TRIALS[ClinicalTrials.gov<br/>Trial Data API]
    end

    subgraph "Security Groups"
        SG1[Frontend SG<br/>Port 443 HTTPS]
        SG2[Backend SG<br/>Port 8080 HTTP]
        SG3[Database SG<br/>Port 5432 PostgreSQL]
    end

    subgraph "Monitoring"
        CW[CloudWatch<br/>Logs & Metrics]
        JOURNAL[Journald<br/>Application Logs]
    end

    USERS --> DNS
    DNS --> CF
    CF --> S3
    CF --> EC2
    
    S3 --> SG1
    EC2 --> SG2
    EC2 --> SYSTEMD
    EC2 --> RDS
    RDS --> SG3
    RDS --> BACKUP
    
    EC2 --> GMAIL
    EC2 --> TRIALS
    
    EC2 --> JOURNAL
    JOURNAL --> CW
    RDS --> CW

    style USERS fill:#e1f5ff
    style CF fill:#FF9900
    style S3 fill:#569A31
    style EC2 fill:#FF9900
    style RDS fill:#527FFF
    style CW fill:#FF4F8B`,
    },
  ];

  const handleDownload = async (index) => {
    const diagramElement = diagramRefs.current[index];
    if (!diagramElement) return;

    const svgElement = diagramElement.querySelector("svg");
    if (!svgElement) return;

    // Get SVG dimensions
    const bbox = svgElement.getBBox();
    const padding = 40;

    // Create canvas
    const canvas = document.createElement("canvas");
    const scale = 3; // Higher resolution
    canvas.width = (bbox.width + padding * 2) * scale;
    canvas.height = (bbox.height + padding * 2) * scale;

    const ctx = canvas.getContext("2d");
    ctx.scale(scale, scale);
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Convert SVG to image
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const img = new Image();
    const svgBlob = new Blob([svgData], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      ctx.drawImage(img, padding, padding);

      // Download
      canvas.toBlob((blob) => {
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `nephrotrax-diagram-${index + 1}.png`;
        link.click();

        URL.revokeObjectURL(url);
        URL.revokeObjectURL(downloadUrl);
      });
    };

    img.src = url;
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div
      style={{
        fontFamily: "Arial, sans-serif",
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "20px",
        backgroundColor: "#f5f5f5",
      }}
    >
      <div
        style={{
          background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
          color: "white",
          padding: "40px",
          borderRadius: "10px",
          marginBottom: "30px",
          textAlign: "center",
        }}
      >
        <h1 style={{ margin: "0 0 10px 0", fontSize: "2.5rem" }}>
          NephroTrax Architecture Diagrams
        </h1>
        <p style={{ margin: 0, fontSize: "1.2rem", opacity: 0.9 }}>
          Patent Application & Technical Documentation
        </p>
      </div>

      <div
        style={{
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "10px",
          marginBottom: "20px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            marginBottom: "10px",
          }}
        >
          {diagrams.map((diagram, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              style={{
                padding: "12px 24px",
                border: "none",
                borderRadius: "5px",
                backgroundColor: activeTab === index ? "#1A5EA8" : "#e0e0e0",
                color: activeTab === index ? "white" : "#333",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: activeTab === index ? "bold" : "normal",
                transition: "all 0.3s",
              }}
            >
              Diagram {index + 1}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={() => handleDownload(activeTab)}
            style={{
              padding: "12px 24px",
              border: "none",
              borderRadius: "5px",
              backgroundColor: "#11998e",
              color: "white",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            📥 Download Current Diagram (PNG)
          </button>
          <button
            onClick={handlePrint}
            style={{
              padding: "12px 24px",
              border: "none",
              borderRadius: "5px",
              backgroundColor: "#0B2545",
              color: "white",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            🖨️ Print All Diagrams
          </button>
        </div>
      </div>

      {diagrams.map((diagram, index) => (
        <div
          key={index}
          style={{
            display: activeTab === index ? "block" : "none",
            backgroundColor: "white",
            padding: "30px",
            borderRadius: "10px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            marginBottom: "20px",
          }}
          className="diagram-page"
        >
          <h2
            style={{
              color: "#1A5EA8",
              marginTop: 0,
              marginBottom: "10px",
              fontSize: "1.8rem",
            }}
          >
            {diagram.title}
          </h2>
          <p
            style={{
              color: "#666",
              marginBottom: "30px",
              fontSize: "1rem",
              lineHeight: "1.6",
            }}
          >
            {diagram.description}
          </p>
          <div
            ref={(el) => (diagramRefs.current[index] = el)}
            className="mermaid"
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "5px",
              overflow: "auto",
            }}
          >
            {diagram.code}
          </div>
        </div>
      ))}

      <div
        style={{
          backgroundColor: "white",
          padding: "30px",
          borderRadius: "10px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          marginTop: "30px",
        }}
      >
        <h2 style={{ color: "#1A5EA8", marginTop: 0 }}>Usage Instructions</h2>
        <div style={{ color: "#666", lineHeight: "1.8" }}>
          <h3>For Patent Application:</h3>
          <ol>
            <li>Click through each diagram tab to review</li>
            <li>Download each diagram as PNG (high resolution)</li>
            <li>Include in patent application as figures</li>
            <li>Reference in detailed description</li>
          </ol>

          <h3>For Presentations:</h3>
          <ul>
            <li>Use Diagram 1 for complete system overview</li>
            <li>Use Diagram 2 for core innovation explanation</li>
            <li>Use Diagram 5 for technical infrastructure</li>
          </ul>

          <h3>For Documentation:</h3>
          <ul>
            <li>Print all diagrams using the Print button</li>
            <li>Save as PDF using browser print dialog</li>
            <li>Include in technical documentation</li>
          </ul>

          <h3>Tips:</h3>
          <ul>
            <li>Downloads are high-resolution (3x scale) for print quality</li>
            <li>White background is included for document compatibility</li>
            <li>Colors are optimized for both screen and print</li>
            <li>Patent applications typically prefer black & white versions</li>
          </ul>
        </div>
      </div>

      <style>{`
        @media print {
          body {
            background: white;
          }
          .diagram-page {
            page-break-after: always;
            display: block !important;
          }
          button {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DiagramViewer;
